/**
 * Performance Formatting Utilities
 * Maps timer step names to user-friendly display names and formats timing data
 */

export interface PerformanceStep {
  step: string;
  duration: number;
  displayName: string;
  category: "initialization" | "preprocessing" | "planning" | "lookup" | "generation" | "formatting" | "total";
  icon: string;
  color: string;
}

/**
 * Map of timer step names to user-friendly display names
 */
const STEP_NAME_MAPPING: Record<string, { name: string; category: PerformanceStep["category"]; icon: string; color: string }> = {
  // Initialization
  session_init: { name: "Session Initialization", category: "initialization", icon: "🔧", color: "gray" },
  chat_history_load_early: { name: "Load Chat History", category: "initialization", icon: "📚", color: "blue" },
  context_data_load: { name: "Load Context Data", category: "initialization", icon: "📦", color: "blue" },
  
  // Preprocessing & Detection
  bot_navigation_detection: { name: "Bot Navigation Detection", category: "preprocessing", icon: "🧭", color: "purple" },
  button_template_check: { name: "Button Template Check", category: "preprocessing", icon: "🔘", color: "purple" },
  more_details_detection: { name: "More Details Detection", category: "preprocessing", icon: "🔍", color: "purple" },
  
  // Planning & Interpretation
  planner_llm: { name: "Planner LLM (Interpretation)", category: "planning", icon: "🧠", color: "green" },
  chat_history_load_backend_button: { name: "Load Chat History (Button)", category: "planning", icon: "📚", color: "blue" },
  
  // Store Identification
  store_identification: { name: "Store Identification", category: "lookup", icon: "🏪", color: "orange" },
  store_identification_main: { name: "Store Identification (Main)", category: "lookup", icon: "🏪", color: "orange" },
  store_identification_backend_button: { name: "Store Identification (Button)", category: "lookup", icon: "🏪", color: "orange" },
  
  // Database Queries & Lookups
  bot_navigation_query: { name: "Bot Navigation Query", category: "lookup", icon: "🗄️", color: "purple" },
  lookups_execution: { name: "Database Lookups", category: "lookup", icon: "🔎", color: "orange" },
  lookups_execution_backend_button: { name: "Database Lookups (Button)", category: "lookup", icon: "🔎", color: "orange" },
  item_details_fetch: { name: "Item Details Fetch", category: "lookup", icon: "📦", color: "orange" },
  store_details_fetch: { name: "Store Details Fetch", category: "lookup", icon: "🏪", color: "orange" },
  
  // Template Processing
  template_response_build: { name: "Template Response Build", category: "formatting", icon: "📋", color: "purple" },
  
  // Final Response Generation
  final_responder_llm: { name: "Final Responder LLM", category: "generation", icon: "✨", color: "pink" },
  final_responder_backend_button: { name: "Final Responder LLM (Button)", category: "generation", icon: "✨", color: "pink" },
  final_responder_bot_navigation: { name: "Final Responder LLM (Navigation)", category: "generation", icon: "✨", color: "pink" },
  final_responder_more_details: { name: "Final Responder LLM (More Details)", category: "generation", icon: "✨", color: "pink" },
  final_responder_more_details_store: { name: "Final Responder LLM (Store Details)", category: "generation", icon: "✨", color: "pink" },
  
  // Response Building
  build_artifacts: { name: "Build Response Artifacts", category: "formatting", icon: "🎨", color: "green" },
  build_artifacts_backend_button: { name: "Build Artifacts (Button)", category: "formatting", icon: "🎨", color: "green" },
  build_artifacts_bot_navigation: { name: "Build Artifacts (Navigation)", category: "formatting", icon: "🎨", color: "green" },
  build_artifacts_more_details: { name: "Build Artifacts (More Details)", category: "formatting", icon: "🎨", color: "green" },
  build_artifacts_more_details_store: { name: "Build Artifacts (Store Details)", category: "formatting", icon: "🎨", color: "green" },
  
  // Session Data
  session_data_fetch: { name: "Session Data Fetch", category: "initialization", icon: "💾", color: "gray" },
  
  // Total
  total_request: { name: "Total Request Time", category: "total", icon: "⏱️", color: "blue" },
};

/**
 * Get user-friendly display name for a timer step
 */
export function getStepDisplayName(stepName: string): string {
  return STEP_NAME_MAPPING[stepName]?.name || stepName;
}

/**
 * Get step category for grouping
 */
export function getStepCategory(stepName: string): PerformanceStep["category"] {
  return STEP_NAME_MAPPING[stepName]?.category || "initialization";
}

/**
 * Format time duration - show milliseconds if < 1s, otherwise show seconds
 */
export function formatDuration(timeMs: number | null | undefined): string {
  if (timeMs === null || timeMs === undefined) return "—";
  if (timeMs < 1000) return `${Math.round(timeMs)}ms`;
  return `${(timeMs / 1000).toFixed(2)}s`;
}

/**
 * Format time duration with color coding based on performance
 */
export function formatDurationWithColor(timeMs: number | null | undefined): { text: string; color: string } {
  if (timeMs === null || timeMs === undefined) return { text: "—", color: "text-gray-400" };
  
  const formatted = formatDuration(timeMs);
  
  if (timeMs < 500) return { text: formatted, color: "text-green-600" };
  if (timeMs < 1000) return { text: formatted, color: "text-blue-600" };
  if (timeMs < 2000) return { text: formatted, color: "text-yellow-600" };
  if (timeMs < 5000) return { text: formatted, color: "text-orange-600" };
  return { text: formatted, color: "text-red-600" };
}

/**
 * Convert performance breakdown array to structured steps with display names
 */
export function formatPerformanceBreakdown(breakdown: Array<{ step: string; duration: number }> | undefined): PerformanceStep[] {
  if (!breakdown || !Array.isArray(breakdown)) return [];
  
  return breakdown
    .filter((item) => item.step !== "total_request") // Exclude total from breakdown
    .map((item) => {
      const mapping = STEP_NAME_MAPPING[item.step] || {
        name: item.step,
        category: "initialization" as const,
        icon: "⚙️",
        color: "gray",
      };
      
      return {
        step: item.step,
        duration: item.duration,
        displayName: mapping.name,
        category: mapping.category,
        icon: mapping.icon,
        color: mapping.color,
      };
    })
    .sort((a, b) => b.duration - a.duration); // Sort by duration descending
}

/**
 * Group performance steps by category
 */
export function groupStepsByCategory(steps: PerformanceStep[]): Record<PerformanceStep["category"], PerformanceStep[]> {
  const grouped: Record<string, PerformanceStep[]> = {
    initialization: [],
    preprocessing: [],
    planning: [],
    lookup: [],
    generation: [],
    formatting: [],
    total: [],
  };
  
  steps.forEach((step) => {
    grouped[step.category].push(step);
  });
  
  return grouped;
}

/**
 * Get category display name
 */
export function getCategoryDisplayName(category: PerformanceStep["category"]): string {
  const names: Record<PerformanceStep["category"], string> = {
    initialization: "Initialization",
    preprocessing: "Preprocessing",
    planning: "Planning",
    lookup: "Database Lookups",
    generation: "Response Generation",
    formatting: "Response Formatting",
    total: "Total",
  };
  return names[category];
}

/**
 * Get category color classes
 */
export function getCategoryColorClasses(category: PerformanceStep["category"]): {
  bg: string;
  border: string;
  text: string;
  badge: string;
} {
  const colors: Record<PerformanceStep["category"], { bg: string; border: string; text: string; badge: string }> = {
    initialization: {
      bg: "bg-gray-50",
      border: "border-gray-200",
      text: "text-gray-700",
      badge: "bg-gray-100 text-gray-700",
    },
    preprocessing: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      text: "text-purple-700",
      badge: "bg-purple-100 text-purple-700",
    },
    planning: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-700",
      badge: "bg-green-100 text-green-700",
    },
    lookup: {
      bg: "bg-orange-50",
      border: "border-orange-200",
      text: "text-orange-700",
      badge: "bg-orange-100 text-orange-700",
    },
    generation: {
      bg: "bg-pink-50",
      border: "border-pink-200",
      text: "text-pink-700",
      badge: "bg-pink-100 text-pink-700",
    },
    formatting: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
      badge: "bg-blue-100 text-blue-700",
    },
    total: {
      bg: "bg-indigo-50",
      border: "border-indigo-200",
      text: "text-indigo-700",
      badge: "bg-indigo-100 text-indigo-700",
    },
  };
  
  return colors[category];
}

